/**
 * HORO-10504: anonymous users' character-access entitlement — mirrors the
 * backend's TierDefinition `anonymous` tier (`characterAliases`, seeded in
 * `seed-tier-definitions-base.ts`) and its own hardcoded mirror in
 * `media-ai-veda-backend/src/service/advisor/access.ts`
 * (`ANONYMOUS_ACCESSIBLE_ADVISOR_ALIASES`) — same tradeoff already made for
 * ANON_TAROT_LIMIT: TierDefinition is still dark-launched (nothing fetches
 * it live), so this is a plain constant rather than a fetched value. Keep
 * all three lists in sync by hand if the roster ever changes.
 *
 * This is enforcement's client-side half only — a UX nicety that avoids a
 * round trip for the common case. The backend's own check
 * (`isAdvisorTierAccessible`) is what a direct API call can't bypass; this
 * one exists so tapping a locked advisor's card shows the register upsell
 * immediately instead of a failed chat-creation request.
 */
export const ANON_ACCESSIBLE_ADVISOR_ALIASES = ['veda', 'ophelia', 'atlas', 'julie', 'estelle'];

/**
 * HORO-10562: registered (free/trial) users' character-access entitlement —
 * mirrors the backend's TierDefinition `registered` tier (`characterAliases`,
 * seeded in `seed-tier-definitions-base.ts`, corrected in
 * `20260914120000-fix-registered-tier-roster.ts`) and its own hardcoded
 * mirror in `media-ai-veda-backend/src/service/advisor/access.ts`
 * (`REGISTERED_ACCESSIBLE_ADVISOR_ALIASES`) — same tradeoff/keep-in-sync-by-
 * hand note as ANON_ACCESSIBLE_ADVISOR_ALIASES above. Anonymous's 5 plus 5
 * more; every advisor NOT in this list is subscriber-exclusive.
 */
export const REGISTERED_ACCESSIBLE_ADVISOR_ALIASES = [
    'veda',
    'ophelia',
    'atlas',
    'julie',
    'estelle',
    'zoe',
    'marie',
    'harmony',
    'avalon',
    'providence'
];

// HORO-10504: Zara (the premium Love Oracle) — kept as its own named
// constant rather than an inline string literal since it's now checked in
// several places (catalog lock UI, chat-start gate).
export const ZARA_ADVISOR_ALIAS = 'zara';

// HORO-10599: Zara's strict, permanent, per-account trial cap for
// registered-free accounts — mirrors the backend's
// `ZARA_TRIAL_MESSAGE_LIMIT` (media-ai-veda-backend's
// service/advisor/zaraTrial.ts). Used client-side only to render the
// message counter and decide when to show the trial-exhausted upsell; the
// real enforcement is server-side.
export const ZARA_TRIAL_MESSAGE_LIMIT = 3;

/**
 * Whether `isUser` may start a chat with `advisorAlias` on the anonymous
 * character-access gate alone — not whether the advisor is visible/
 * discoverable at all, and not Zara's separate premium-subscriber gate, and
 * not the registered-tier roster cap (see `getAdvisorLockReason` below,
 * which layers all of that on top). Zara's own visibility gate (feature-flag
 * rollout, hides her entirely) is unrelated and unaffected — this never
 * returns `false` for her specifically; the backend 404s her for the wrong
 * audience before this would even matter.
 */
export const canStartChatWithAdvisor = (advisorAlias, isUser) => {
    if (isUser || !advisorAlias) {
        return true;
    }
    return ANON_ACCESSIBLE_ADVISOR_ALIASES.includes(advisorAlias);
};

/**
 * Whether `user` currently holds a paying (non-free) plan. `plan.isFree` is
 * the only tier signal the frontend has today (see `setPaymentPopupState` in
 * authSlice.js) — true for anonymous/registered-free/trial-in-progress
 * accounts, false once a subscription is actually paying. Anonymous users
 * have no `plan` at all, so they fail the `user?.id` check first.
 */
export const isPremiumSubscriber = (user) => !!user?.id && !user?.plan?.isFree;

// Why (if at all) an advisor's catalog card is locked, so the card can pick
// the right lock icon + CTA copy ('register to chat' vs 'subscribe to
// chat'). `null` means unlocked.
export const ADVISOR_LOCK_REASON = {
    REGISTER: 'register',
    SUBSCRIBE: 'subscribe'
};

/**
 * `getAdvisorLockReason` — the catalog-card counterpart to
 * `canStartChatWithAdvisor`, folding in Zara's premium-subscriber gate and
 * (HORO-10562) the registered-tier roster cap on top of it. Zara is checked
 * first and short-circuits, but (HORO-10599) she's no longer a flat
 * SUBSCRIBE for everyone who isn't premium:
 *   - anonymous viewers get REGISTER (she's the conversion funnel — sign up
 *     for a 3-message free trial, not a paywall);
 *   - registered-free viewers get `null` (unlocked) until their trial is
 *     exhausted, then SUBSCRIBE.
 * Any premium subscriber is unlocked for every advisor (subscriber/
 * subscriber_legacy have `hasAllCharacters: true`, TierDefinition-side).
 * Anonymous callers check `ANON_ACCESSIBLE_ADVISOR_ALIASES` first; if that
 * fails, an anon viewer only gets the REGISTER cta when registering would
 * actually unlock the advisor (i.e. it's also in
 * `REGISTERED_ACCESSIBLE_ADVISOR_ALIASES`) — an advisor outside BOTH lists
 * (e.g. Otis) is subscriber-exclusive, so anon and registered-free viewers
 * alike get the SUBSCRIBE cta rather than a REGISTER cta that wouldn't
 * actually grant access.
 */
export const getAdvisorLockReason = (advisorAlias, { isUser, isPremiumUser, zaraTrialExhausted = false }) => {
    if (!advisorAlias) {
        return null;
    }
    if (advisorAlias === ZARA_ADVISOR_ALIAS) {
        if (isPremiumUser) {
            return null;
        }
        if (!isUser) {
            return ADVISOR_LOCK_REASON.REGISTER;
        }
        return zaraTrialExhausted ? ADVISOR_LOCK_REASON.SUBSCRIBE : null;
    }
    if (isPremiumUser) {
        return null;
    }
    const isRegisteredAccessible = REGISTERED_ACCESSIBLE_ADVISOR_ALIASES.includes(advisorAlias);
    if (!isUser) {
        if (canStartChatWithAdvisor(advisorAlias, isUser)) {
            return null;
        }
        return isRegisteredAccessible ? ADVISOR_LOCK_REASON.REGISTER : ADVISOR_LOCK_REASON.SUBSCRIBE;
    }
    return isRegisteredAccessible ? null : ADVISOR_LOCK_REASON.SUBSCRIBE;
};

/**
 * HORO-10563: which advisors CatalogPageV2 badges "Premium" — a property of
 * the advisor, independent of the viewer (unlike `getAdvisorLockReason`,
 * which answers "can THIS viewer chat with them"). Today this happens to be
 * exactly the Zara subscription gate, but it's kept as its own list rather
 * than inferred from `getAdvisorLockReason`/`REGISTERED_ACCESSIBLE_ADVISOR_ALIASES`
 * because a premium *subscriber* still sees the Premium tag on Zara's card —
 * the tag describes the advisor, the lock reason describes the viewer, and
 * collapsing them would hide the tag for the one audience most likely to
 * recognize/want it. Mirrors the backend's TierDefinition the same
 * keep-in-sync-by-hand way as ANON_/REGISTERED_ACCESSIBLE_ADVISOR_ALIASES above.
 */
export const PREMIUM_ADVISOR_ALIASES = [ZARA_ADVISOR_ALIAS];
export const isPremiumAdvisor = (advisorAlias) => PREMIUM_ADVISOR_ALIASES.includes(advisorAlias);

/**
 * Whether the one-time "first time in Zara" welcome screen (ZaraWelcomeModal)
 * should intercept a premium subscriber's tap instead of taking them straight
 * to chat — i.e. `getAdvisorLockReason` already returned `null` for this
 * alias (unlocked) and the account hasn't seen it before. `seenAdvisorIntros`
 * is the backend-persisted list (authSlice's `getSeenAdvisorIntros`,
 * `markAdvisorIntroSeen`), not a localStorage flag — the whole point is
 * "never see it more than once," account-wide, not per-device.
 *
 * Registered-free accounts do NOT get this modal (reverted HORO-10599
 * attempt) — their first-time welcome + chips live inline in the chat
 * itself instead, the same in-thread cold-start treatment anonymous users
 * already get (see ChatPage's `showColdStartInChat`), not a gate in front
 * of it.
 */
export const shouldShowZaraWelcome = (advisorAlias, { isPremiumUser, seenAdvisorIntros }) =>
    advisorAlias === ZARA_ADVISOR_ALIAS && !!isPremiumUser && !(seenAdvisorIntros || []).includes(ZARA_ADVISOR_ALIAS);
